import React, { useState, useEffect, useContext } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  Space,
  Button,
  Tag,
  Progress,
  Table,
  Select,
  DatePicker,
  Tooltip,
  Modal,
  List,
  Avatar,
  Divider,
  Alert,
  Tabs,
  message,
  Spin,
  Input,
  Drawer,
  Descriptions,
  Badge
} from 'antd';
import {
  DashboardOutlined,
  TeamOutlined,
  UserOutlined,
  DollarOutlined,
  MessageOutlined,
  WhatsAppOutlined,
  RiseOutlined,
  FallOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  BankOutlined,
  ShoppingCartOutlined,
  ApiOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  SendOutlined,
  PhoneOutlined,
  MailOutlined,
  GlobalOutlined,
  BarChartOutlined,
  PieChartOutlined,
  LineChartOutlined,
  DownloadOutlined,
  FilterOutlined,
  ReloadOutlined,
  SettingOutlined,
  CrownOutlined,
  ShopOutlined,
  LinkOutlined,
  ExclamationCircleOutlined,
  SafetyOutlined
} from '@ant-design/icons';

import { AuthContext } from '../../../../../context/authContext';
import { useUserContext, hasPageAccess } from '../../../../../context/userContext';
import axiosConfig from '../../../../../config/axiosConfig';
import CustomerDetailsTable from './CustomerDetailsTable';
import PartnerAnalyticsTable from './PartnerAnalyticsTable';
import RevenueAnalyticsDashboard from './RevenueAnalyticsDashboard';
import APIUsageAnalytics from './APIUsageAnalytics';
import SubscriptionPlansPage from './SubscriptionPlansPage';
import AdminUserManagement from './AdminUserManagement';
import PaymentManagement from './PaymentManagement';
import AuditLogs from './AuditLogs';
import './SuperAdminDashboard.css';
import './AdminDashboard.css';
import moment from 'moment';

import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, Legend,
} from 'recharts';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

const SuperAdminDashboard = () => {
  const { currentUser } = useContext(AuthContext);
  const { userCtx } = useUserContext();
  const [loading, setLoading] = useState(false);
  const [timeRange, setTimeRange] = useState('30d');
  const [dateRange, setDateRange] = useState([moment().subtract(30, 'days'), moment()]);
  
  // Dashboard data from API
  const [dashboardCounts, setDashboardCounts] = useState({
    totalcustomer: 0,
    directcustomer: 0,
    solutioncustomer: 0,
    newcustomerthismonth: 0,
    allactivecustomer: 0,
    allpendingcustomer: 0,
    customerviasolution: 0,
    activesolutioncustomer: 0,
    pendingsolutioncustomer: 0,
    activedirectcustomer: 0,
    pendingdirectcustomer: 0,
    monthlycustomer: []
  });

  // Monthly chart data state
  const [monthlyChartData, setMonthlyChartData] = useState([]);

  // Current selected view and table data
  const [selectedView, setSelectedView] = useState('total');
  const [tableData, setTableData] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);

  // Modal states
  const [detailModal, setDetailModal] = useState({ visible: false, type: '', data: [] });
  const [customerModal, setCustomerModal] = useState({ visible: false, filterType: '', data: null, loading: false });
  const [partnerModal, setPartnerModal] = useState({ visible: false, filterType: '', data: null, loading: false });
  const [revenueModal, setRevenueModal] = useState({ visible: false, filterType: '' });
  const [apiModal, setApiModal] = useState({ visible: false, filterType: '' });
  const [referredCompaniesModal, setReferredCompaniesModal] = useState({ visible: false, data: [], loading: false, partnerName: '' });

  // Company Detail Drawer state
  const [companyDetailDrawer, setCompanyDetailDrawer] = useState({ visible: false, company: null });
  const [wabaDetail, setWabaDetail] = useState(null);
  const [loadingWaba, setLoadingWaba] = useState(false);
  const [updatingWabaStatus, setUpdatingWabaStatus] = useState(false);
  const [subscribingWebhooks, setSubscribingWebhooks] = useState(false);

  const hasAdminTabAccess = (pageKey) => !userCtx?.pages || hasPageAccess(userCtx, pageKey);
  const adminDefaultTab = hasAdminTabAccess('admin')
    ? 'overview'
    : hasAdminTabAccess('platform_plans')
      ? 'subscription-plans'
      : hasAdminTabAccess('topup_plans')
        ? 'topup-plans'
        : hasAdminTabAccess('payment_management')
          ? 'payment-management'
          : 'overview';


  // Company details state
  const [companyDetails, setCompanyDetails] = useState({
    companies: [],
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalRecords: 0,
      limit: 20,
      hasNextPage: false,
      hasPrevPage: false
    },
    totalrecords: 0,
    totalreffredcustomer: 0,
    totalsolutionpartners: 0
  });

  // Chart data
  const [chartData, setChartData] = useState({
    customerGrowth: [
      { month: 'Jan', customers: 1200, revenue: 45000 },
      { month: 'Feb', customers: 1350, revenue: 52000 },
      { month: 'Mar', customers: 1580, revenue: 61000 },
      { month: 'Apr', customers: 1750, revenue: 68000 },
      { month: 'May', customers: 1920, revenue: 75000 },
      { month: 'Jun', customers: 2180, revenue: 84000 }
    ],
    apiUsageDistribution: [
      { name: 'Send Message', value: 45, color: '#8884d8' },
      { name: 'Media Upload', value: 25, color: '#82ca9d' },
      { name: 'Template', value: 15, color: '#ffc658' },
      { name: 'Webhook', value: 10, color: '#ff7300' },
      { name: 'Others', value: 5, color: '#00ff00' }
    ],
    revenueBreakdown: [
      { category: 'Premium Plans', amount: 125000, percentage: 45 },
      { category: 'API Usage', amount: 89000, percentage: 32 },
      { category: 'Enterprise', amount: 67000, percentage: 23 }
    ]
  });

  // Partner applications data
  const [partnerApplications, setPartnerApplications] = useState([
    {
      id: 1,
      partnerName: 'TechSolutions Inc.',
      supportEmail: 'support@techsolutions.com',
      primaryColor: '#1890ff',
      applicationDate: '2025-09-15',
      status: 'pending_review',
      businessConfig: {
        port: 465,
        smtp_host: 'mail.techsolutions.com',
        ssl_enabled: true
      },
      priority: 'high'
    },
    {
      id: 2,
      partnerName: 'Digital Marketing Pro',
      supportEmail: 'hello@digitalmarketing.com',
      primaryColor: '#52c41a',
      applicationDate: '2025-09-14',
      status: 'approved',
      businessConfig: {
        port: 587,
        smtp_host: 'smtp.digitalmarketing.com',
        ssl_enabled: true
      },
      priority: 'medium'
    },
    {
      id: 3,
      partnerName: 'CloudSync Solutions',
      supportEmail: 'support@cloudsync.io',
      primaryColor: '#fa8c16',
      applicationDate: '2025-09-13',
      status: 'rejected',
      businessConfig: {
        port: 465,
        smtp_host: 'mail.cloudsync.io',
        ssl_enabled: false
      },
      priority: 'low',
      rejectionReason: 'Incomplete documentation'
    }
  ]);

  // Customer pending approvals data
  const [pendingApprovals, setPendingApprovals] = useState([
    {
      id: 1,
      customerName: 'TechCorp Solutions',
      businessType: 'Technology',
      requestDate: '2025-09-10',
      planType: 'Enterprise',
      status: 'verification_pending',
      documents: ['business_license', 'tax_certificate'],
      priority: 'high'
    },
    {
      id: 2,
      customerName: 'Digital Marketing Pro',
      businessType: 'Marketing',
      requestDate: '2025-09-09',
      planType: 'Premium',
      status: 'document_review',
      documents: ['business_license'],
      priority: 'medium'
    },
    {
      id: 3,
      customerName: 'E-commerce Hub',
      businessType: 'Retail',
      requestDate: '2025-09-08',
      planType: 'Business',
      status: 'compliance_check',
      documents: ['business_license', 'tax_certificate', 'compliance_docs'],
      priority: 'high'
    },
    {
      id: 4,
      customerName: 'Healthcare Connect',
      businessType: 'Healthcare',
      requestDate: '2025-09-07',
      planType: 'Enterprise',
      status: 'final_approval',
      documents: ['business_license', 'healthcare_license'],
      priority: 'critical'
    }
  ]);

  // Dashboard data state
  const [dashboardData, setDashboardData] = useState({
    // Customer Analytics
    totalCustomers: 1247,
    activeCustomers: 987,
    inactiveCustomers: 260,
    newCustomersThisMonth: 85,
    
    // Solution Partner Analytics
    totalSolutionPartners: 42,
    activeSolutionPartners: 38,
    inactiveSolutionPartners: 4,
    newPartnersThisMonth: 3,
    
    // Revenue Analytics
    totalRevenue: 245680.50,
    monthlyRecurringRevenue: 98756.20,
    averageRevenuePerCustomer: 197.15,
    partnerGeneratedRevenue: 67890.30,
    
    // WhatsApp API Usage
    totalMessagesProcessed: 2456789,
    monthlyMessages: 456123,
    dailyMessages: 15678,
    apiCallsThisMonth: 1234567,
    
    // System Performance
    systemUptime: 99.97,
    apiResponseTime: 245,
    errorRate: 0.23,
    activeConnections: 1456,
    
    // Billing & Cost Analytics
    totalBillingAmount: 123456.78,
    outstandingPayments: 12345.67,
    averagePaymentTime: 15.5,
    churnRate: 2.3,
    
    // Trends
    trends: {
      customers: { change: 12.5, direction: 'up' },
      partners: { change: 8.3, direction: 'up' },
      revenue: { change: 23.7, direction: 'up' },
      messages: { change: 15.2, direction: 'up' },
      apiUsage: { change: 18.9, direction: 'up' },
      systemHealth: { change: 0.1, direction: 'up' }
    }
  });

  // Fetch dashboard data
  useEffect(() => {
    if (!hasAdminTabAccess('admin')) return;
    fetchDashboardCounts();
    fetchMonthlySignupData();
    fetchTableData('total'); // Load total customers by default
    fetchPartnerApplications(); // Load partner applications
  }, []);

  const fetchDashboardCounts = async () => {
    setLoading(true);
    try {
      const response = await axiosConfig.get('/dashboard/counts');
      
      if (response.data?.status === true) {
        setDashboardCounts(prev => ({
          ...prev,
          ...(response.data.entity || {})
        }));
      }
    } catch (error) {
      console.error('Error fetching dashboard counts:', error);
      message.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch partner applications
  const fetchPartnerApplications = async () => {
    try {
      const response = await axiosConfig.get('/dashboard/partner-request');
      
      if (response.data?.success) {
        // Transform the data to match our component structure
        const transformedData = response.data.data?.map(item => ({
          id: item.id,
          partnerName: item.partner_name,
          supportEmail: item.support_email,
          primaryColor: item.primary_color,
          applicationDate: item.created_at,
          status: item.status === 0 ? 'pending_review' : item.status === 1 ? 'approved' : 'rejected',
          businessConfig: item.business_config ? JSON.parse(item.business_config) : {},
          brandingConfig: item.branding_config ? JSON.parse(item.branding_config) : {},
          customeruuid: item.customeruuid,
          slug: item.slug,
          priority: 'medium', // Default priority
          rejectionReason: item.rejection_reason || null
        })) || [];
        
        setPartnerApplications(transformedData);
      }
    } catch (error) {
      console.error('Error fetching partner applications:', error);
      // Don't show error message as this might be called in background
    }
  };

  // Handle partner application approval
  const handleApproveApplication = async (applicationId, partnerName) => {
    try {
      const response = await axiosConfig.put(`/dashboard/partner-request/${applicationId}`, {
        status: 1, // 1 = approved
        action: 'approve'
      });
      
      if (response.data?.success) {
        message.success('Partner application approved successfully!');
        fetchPartnerApplications(); // Refresh data
      } else {
        message.error(response.data?.message || 'Failed to approve application');
      }
    } catch (error) {
      console.error('Error approving application:', error);
      message.error('Failed to approve application');
    }
  };

  // Handle partner application rejection
  const handleRejectApplication = async (applicationId, partnerName, rejectionReason) => {
    try {
      const response = await axiosConfig.put(`/dashboard/partner-request/${applicationId}`, {
        status: 2, // 2 = rejected
        action: 'reject',
        rejection_reason: rejectionReason
      });
      
      if (response.data?.success) {
        message.success('Partner application rejected.');
        fetchPartnerApplications(); // Refresh data
      } else {
        message.error(response.data?.message || 'Failed to reject application');
      }
    } catch (error) {
      console.error('Error rejecting application:', error);
      message.error('Failed to reject application');
    }
  };

  // Open company detail drawer and fetch WABA details
  const openCompanyDetail = async (record) => {
    setCompanyDetailDrawer({ visible: true, company: record });
    setWabaDetail(null);
    await fetchCompanyWabaDetails(record.customeruuid);
  };

  const closeCompanyDetail = () => {
    setCompanyDetailDrawer({ visible: false, company: null });
    setWabaDetail(null);
  };

  // Fetch WABA details for a specific company (admin endpoint)
  const fetchCompanyWabaDetails = async (customeruuid) => {
    if (!customeruuid) return;
    try {
      setLoadingWaba(true);
      const response = await axiosConfig.post('/admin/companywaba/details', { customeruuid });
      if (response.data?.status === true && response.data?.entity) {
        setWabaDetail(response.data.entity);
      } else if (response.data?.success && response.data?.data) {
        setWabaDetail(response.data.data);
      } else {
        setWabaDetail(null);
      }
    } catch (error) {
      console.error('Error fetching company WABA details:', error);
      setWabaDetail(null);
    } finally {
      setLoadingWaba(false);
    }
  };

  // Update WABA status for a specific company
  const updateWabaStatus = async (companyId, newStatus) => {
    try {
      setUpdatingWabaStatus(true);
      const response = await axiosConfig.put(`/admin/companies/${companyId}/waba-status`, {
        waba_status: newStatus
      });
      if (response.data?.success || response.data?.status === true) {
        const successMessages = {
          active: 'WABA activated successfully. The company can now access WhatsApp modules.',
          pending: 'WABA status set to pending.',
          inactive: 'WABA deactivated successfully.'
        };
        message.success(successMessages[newStatus] || 'WABA status updated successfully.');
        // Refresh WABA details
        if (companyDetailDrawer.company?.customeruuid) {
          await fetchCompanyWabaDetails(companyDetailDrawer.company.customeruuid);
        }
      } else {
        message.error(response.data?.message || 'Failed to update WABA status.');
      }
    } catch (error) {
      console.error('Error updating WABA status:', error);
      message.error('Failed to update WABA status. Please try again.');
    } finally {
      setUpdatingWabaStatus(false);
    }
  };

  const confirmActivateWaba = (companyId) => {
    Modal.confirm({
      title: 'Activate WhatsApp Access',
      icon: <WhatsAppOutlined style={{ color: '#25D366' }} />,
      content: 'Are you sure you want to activate WhatsApp access for this company?',
      okText: 'Activate',
      okButtonProps: { style: { backgroundColor: '#25D366', borderColor: '#25D366' } },
      cancelText: 'Cancel',
      onOk: () => updateWabaStatus(companyId, 'active'),
    });
  };

  const subscribeWabaWebhooks = async (companyId) => {
    try {
      setSubscribingWebhooks(true);
      const response = await axiosConfig.post(`/admin/companies/${companyId}/subscribe-waba-webhooks`);
      if (response.data?.status === true || response.data?.statuscode === 200) {
        const subscribedEntity = response.data?.entity || {};
        // Reflect activation immediately in UI; details endpoint can lag briefly after subscription.
        setWabaDetail((prev) => ({
          ...(prev || {}),
          ...subscribedEntity,
          waba_status: subscribedEntity.waba_status || 'active',
        }));
        message.success(response.data?.message || 'Webhooks subscribed successfully. WABA is now active.');
      } else {
        message.error(response.data?.message || 'Failed to subscribe WABA webhooks.');
      }
    } catch (error) {
      console.error('Error subscribing WABA webhooks:', error);
      message.error(error.response?.data?.message || 'Failed to subscribe WABA webhooks. Please try again.');
    } finally {
      setSubscribingWebhooks(false);
    }
  };

  const confirmSubscribeWabaWebhooks = (companyId) => {
    Modal.confirm({
      title: 'Subscribe WABA Webhooks',
      icon: <WhatsAppOutlined style={{ color: '#25D366' }} />,
      content: 'This will subscribe the company\'s WhatsApp Business Account to webhooks and set the WABA status to active. Proceed?',
      okText: 'Subscribe',
      okButtonProps: { style: { backgroundColor: '#25D366', borderColor: '#25D366' } },
      cancelText: 'Cancel',
      onOk: () => subscribeWabaWebhooks(companyId),
    });
  };

  const confirmChangeWabaStatus = (companyId, newStatus) => {
    const labels = { pending: 'set to Pending', inactive: 'deactivate (Inactive)' };
    Modal.confirm({
      title: 'Change WABA Status',
      content: `Are you sure you want to ${labels[newStatus] || 'update'} WhatsApp access for this company?`,
      okText: 'Confirm',
      okType: newStatus === 'inactive' ? 'danger' : 'primary',
      cancelText: 'Cancel',
      onOk: () => updateWabaStatus(companyId, newStatus),
    });
  };

  // Fetch monthly signup data from dedicated API
  const fetchMonthlySignupData = async () => {
    try {
      const response = await axiosConfig.get('/dashboard/monthly-signup-count');
      
      if (response.data?.status === true) {
        const monthlyData = (response.data.entity || [])
          .map(item => ({
            month: item.month_year,
            totalCustomers: parseInt(item.totalcustomer) || 0,
            directCustomers: parseInt(item.totaldirectcustomer) || 0,
            solutionPartners: parseInt(item.totalsolutionpartner) || 0
          }))
          .reverse(); // reverse to show oldest → newest left to right
        
        setMonthlyChartData(monthlyData);
      }
    } catch (error) {
      console.error('Error fetching monthly signup data:', error);
      message.error('Failed to load monthly signup data');
    }
  };

  const fetchTableData = async (viewType) => {
    setTableLoading(true);
    try {
      let companyType = 'all';
      const requestBody = {
        page: 1,
        limit: 100
      };
      
      switch (viewType) {
        case 'direct':
          companyType = 'direct_customer';
          requestBody.companytype = companyType;
          break;
        case 'solution':
          companyType = 'solution_partner';
          requestBody.companytype = companyType;
          break;
        case 'active-pending':
          // For active-pending, we need companystatus
          requestBody.companystatus = 'all';
          break;
        default:
          // For total customers, don't pass companytype or companystatus
          break;
      }

      const response = await axiosConfig.post('/company/getcompanydetails', requestBody);
      
      if (response.data?.status === true && response.data?.statuscode === 200) {
        const data = response.data.entity;
        setTableData(data.companies || []);
        setCompanyDetails(data);
      }
    } catch (error) {
      console.error('Error fetching table data:', error);
      message.error('Failed to load table data');
    } finally {
      setTableLoading(false);
    }
  };

  // Fetch company details based on type
  const fetchCompanyDetails = async (companyType, status = null, page = 1, limit = 20, partnerid = null) => {
    try {
      const requestBody = {
        page: page,
        limit: limit  
      };
      // Only add companytype if specified
      if (companyType) {
        requestBody.companytype = companyType;
      }

      // Only add companystatus if specified
      if (status) {
        requestBody.companystatus = status;
      }

      // Only add partnerid if specifically provided (for referred companies)
      if (partnerid) {
        requestBody.partnerid = partnerid;
      }

      const response = await axiosConfig.post('/company/getcompanydetails', requestBody);
      
      if (response.data?.status === true && response.data?.statuscode === 200) {
        return response.data.entity;
      }
      return null;
    } catch (error) {
      console.error('Error fetching company details:', error);
      message.error('Failed to load company details');
      return null;
    }
  };

  // Fetch referred companies for a specific partner
  const fetchReferredCompanies = async (partnerid, partnerName) => {
    setReferredCompaniesModal(prev => ({ ...prev, loading: true, visible: true, partnerName }));
    try {
      // For referred companies, use companytype: 'all' and don't pass companystatus
      const data = await fetchCompanyDetails('all', null, 1, 100, partnerid);
      if (data) {
        setReferredCompaniesModal(prev => ({ 
          ...prev, 
          data: data.companies || [], 
          loading: false 
        }));
      } else {
        setReferredCompaniesModal(prev => ({ ...prev, data: [], loading: false }));
      }
    } catch (error) {
      console.error('Error fetching referred companies:', error);
      message.error('Failed to load referred companies');
      setReferredCompaniesModal(prev => ({ ...prev, data: [], loading: false }));
    }
  };

  // Transform API data to match component expectations
  const transformPartnerData = (apiData) => {
    if (!apiData || !apiData.companies) return apiData;

    const transformedCompanies = apiData.companies.map(company => {
      // Calculate tier based on customer count
      const customerCount = company.totalcustomer || 0;
      let tier = 'Bronze';
      if (customerCount >= 20) tier = 'Platinum';
      else if (customerCount >= 15) tier = 'Gold';
      else if (customerCount >= 10) tier = 'Silver';

      // Transform status
      const status = company.status === 1 ? 'Active' : 'Pending';

      return {
        ...company,
        id: company.companyid,
        partnerName: company.companyname || 'Unknown Partner',
        contactPerson: company.contactperson || 'N/A',
        email: company.emailid || 'N/A',
        phone: company.contactno || 'N/A',
        status: status,
        tier: tier,
        joinDate: company.entrytime || new Date().toISOString(),
        lastActive: company.updatetime || new Date().toISOString(),
        customersReferred: company.totalcustomer || 0,
        activeCustomers: company.activecustomer || 0,
        inactiveCustomers: (company.totalcustomer || 0) - (company.activecustomer || 0),
        totalRevenue: (company.activecustomer || 0) * 50, // Mock calculation
        monthlyRevenue: (company.activecustomer || 0) * 10, // Mock calculation
        commission: (company.activecustomer || 0) * 5, // Mock calculation
        commissionRate: 10,
        conversionRate: company.totalcustomer > 0 ? ((company.activecustomer || 0) / company.totalcustomer * 100) : 0,
        averageCustomerValue: company.activecustomer > 0 ? ((company.activecustomer || 0) * 50) / company.activecustomer : 0,
        totalMessages: (company.activecustomer || 0) * 1000, // Mock calculation
        monthlyMessages: (company.activecustomer || 0) * 100, // Mock calculation
        supportTickets: Math.floor(Math.random() * 5), // Mock data
        rating: 4.0 + Math.random(), // Mock data
        certificationLevel: customerCount >= 15 ? 'Advanced' : customerCount >= 10 ? 'Intermediate' : 'Basic',
        territoryFocus: 'Global', // Mock data
        specialization: ['General'], // Mock data
        performanceScore: Math.min(100, 60 + (company.activecustomer || 0) * 2), // Mock calculation
        billingStatus: status === 'Active' ? 'Paid' : 'Pending',
        nextPaymentDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() // 30 days from now
      };
    });

    return {
      ...apiData,
      companies: transformedCompanies
    };
  };

  // Handle card click to change table view
  const handleCardClick = async (viewType) => {
    setSelectedView(viewType);
    await fetchTableData(viewType);
  };

  // Handle navigation to pending customers page with API call
  const handleNavigateToPendingCustomers = async () => {
    try {
      setLoading(true);
      const requestBody = {
        companystatus: "pending",
        page: 1,
        limit: 100
      };

      const response = await axiosConfig.post('/company/getcompanydetails', requestBody);
      
      if (response.data?.status === true && response.data?.statuscode === 200) {
        // Navigate to pending customers page
        window.location.href = '/dashboard/admin/pending-customers';
      } else {
        message.error('Failed to load pending customers');
      }
    } catch (error) {
      console.error('Error fetching pending customers:', error);
      message.error('Failed to load pending customers');
    } finally {
      setLoading(false);
    }
  };


  // Monthly customer chart data is now managed by state and API call

  // Table columns for customer data
  const getTableColumns = () => {
    const baseColumns = [
      {
        title: 'Sr. No.',
        key: 'srno',
        width: 60,
        render: (_, record, index) => (
          <div style={{
            textAlign: 'center',
            fontWeight: 700,
            fontSize: 12,
            color: '#6b7280',
            background: '#f3f4f6',
            borderRadius: 6,
            padding: '2px 6px',
            display: 'inline-block',
            minWidth: 28,
          }}>
            {index + 1}
          </div>
        ),
      },
      {
        title: 'Company Details',
        key: 'company',
        render: (_, record) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 8,
              background: '#e6f7ff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
              fontSize: 14, fontWeight: 700, color: '#1890ff'
            }}>
              {(record.companyname || 'U').charAt(0).toUpperCase()}
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#1a1d26', lineHeight: 1.4 }}>
                {record.companyname || 'Unknown Company'}
              </div>
              <div style={{ fontSize: 12, color: '#8c8c8c', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', lineHeight: 1.3, marginTop: 2 }}>
                <span>{record.emailid || 'N/A'}</span>
                {record.contactno && (
                  <><span style={{ color: '#d0d0d0' }}>&middot;</span>
                  <span>{record.countrycode || ''}{record.contactno}</span></>
                )}
              </div>
            </div>
          </div>
        ),
      },
      {
        title: 'Status',
        key: 'status',
        render: (_, record) => (
          <Tag color={record.status === 1 ? 'green' : 'orange'}>
            {record.status === 1 ? 'Active' : 'Pending'}
          </Tag>
        ),
      },
      {
        title: 'Verification',
        key: 'verification',
        render: (_, record) => (
          <Tag color={record.isverified === 1 ? 'blue' : 'orange'}>
            {record.isverified === 1 ? 'Verified' : 'Unverified'}
          </Tag>
        ),
      },
      {
        title: 'Type',
        key: 'type',
        render: (_, record) => (
          <Tag color={record.company_type === 'SOLUTION_PARTNER' ? 'purple' : 'blue'}>
            {record.company_type === 'SOLUTION_PARTNER' ? 'Solution Partner' : 'Direct Customer'}
          </Tag>
        ),
      },
      {
        title: 'Join Date',
        key: 'joinDate',
        render: (_, record) => (
          <div style={{ fontSize: '12px' }}>
            {moment(record.entrytime).format('MMM DD, YYYY')}
          </div>
        ),
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 100,
        render: (_, record) => (
          <Tooltip title="View Details">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => openCompanyDetail(record)}
            >
              Details
            </Button>
          </Tooltip>
        ),
      }
    ];

    // Add customer metrics column for solution partners and total view
    if (selectedView === 'solution' || selectedView === 'total') {
      baseColumns.splice(4, 0, {
        title: 'Customer Metrics',
        key: 'metrics',
        render: (_, record) => (
          <div style={{ fontSize: 13, display: 'flex', flexDirection: 'column', gap: 4, lineHeight: 1.3 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: '#8c8c8c' }}>Total:</span>
              <Text
                style={{
                  fontWeight: 600,
                  fontSize: 13,
                  display: 'inline-block',
                  cursor: record.totalcustomer > 0 ? 'pointer' : 'default',
                  color: record.totalcustomer > 0 ? '#1890ff' : '#666',
                }}
                onClick={() => {
                  if (record.totalcustomer > 0) {
                    fetchReferredCompanies(record.companyid, record.companyname);
                  }
                }}
              >
                {record.totalcustomer || 0}
              </Text>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <span><span style={{ color: '#52c41a' }}>Active:</span> <span style={{ color: '#52c41a', fontWeight: 600 }}>{record.activecustomer || 0}</span></span>
              <span><span style={{ color: '#ff4d4f' }}>Pending:</span> <span style={{ color: '#ff4d4f', fontWeight: 600 }}>{record.pendingcustomer || 0}</span></span>
            </div>
          </div>
        ),
      });
    }

    // Add parent partner info for solution partners
    if (selectedView === 'solution') {
      baseColumns.push({
        title: 'Parent Partner',
        key: 'parent',
        render: (_, record) => (
          <div style={{ fontSize: '12px' }}>
            {record.parent_partner_id ? (
              <Text style={{ color: '#1890ff' }}>
                {record.parent_partner_id.substring(0, 8)}...
              </Text>
            ) : (
              <Text type="secondary">None</Text>
            )}
          </div>
        ),
      });
    }

    return baseColumns;
  };

  // Enhanced Customer Analytics Cards
  const customerWidgets = [
    {
      id: 'total',
      title: 'Total Customers',
      value: dashboardCounts.totalcustomer || 0,
      icon: <TeamOutlined />,
      color: '#1890ff',
      bgColor: '#e6f7ff',
      description: 'Direct customers + Solution partners',
      subtitle: `${dashboardCounts.directcustomer || 0} Direct + ${dashboardCounts.solutioncustomer || 0} Partners`,
      onClick: () => handleCardClick('total')
    },
    {
      id: 'direct',
      title: 'Direct Customers',
      value: dashboardCounts.directcustomer || 0,
      icon: <UserOutlined />,
      color: '#52c41a',
      bgColor: '#f6ffed',
      description: 'Customers registered directly',
      subtitle: 'Self-registered accounts',
      onClick: () => handleCardClick('direct')
    },
    {
      id: 'solution',
      title: 'Solution Partners',
      value: dashboardCounts.solutioncustomer || 0,
      icon: <CrownOutlined />,
      color: '#722ed1',
      bgColor: '#f9f0ff',
      description: 'Solution partner accounts',
      subtitle: 'Partner network members',
      onClick: () => handleCardClick('solution')
    },
    {
      id: 'status',
      title: 'Active/Pending Status',
      value: `${dashboardCounts.allactivecustomer || 0}/${dashboardCounts.partnerrequestpending || 0}`,
      icon: <CheckCircleOutlined />,
      color: '#fa8c16',
      bgColor: '#fff7e6',
      description: 'Active vs Pending customers',
      subtitle: `${dashboardCounts.allactivecustomer || 0} Active • ${dashboardCounts.partnerrequestpending || 0} Pending`,
      isStatusCard: true,
      activeCount: dashboardCounts.allactivecustomer || 0,
      pendingCount: dashboardCounts.partnerrequestpending || 0,
      onClick: () => handleNavigateToPendingCustomers()
    }
  ];

  // Customer approval handling functions
  const handleApproveCustomer = async (customerId) => {
    try {
      setLoading(true);
      // API call to approve customer
      message.success('Customer approved successfully');
      setPendingApprovals(prev => prev.filter(item => item.id !== customerId));
    } catch (error) {
      message.error('Failed to approve customer');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectCustomer = async (customerId, reason) => {
    try {
      setLoading(true);
      // API call to reject customer
      message.success('Customer request rejected');
      setPendingApprovals(prev => prev.filter(item => item.id !== customerId));
    } catch (error) {
      message.error('Failed to reject customer');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestMoreInfo = async (customerId, requiredDocs) => {
    try {
      setLoading(true);
      // API call to request more information
      message.success('Information request sent to customer');
    } catch (error) {
      message.error('Failed to send information request');
    } finally {
      setLoading(false);
    }
  };

  // Handle navigation
  const handleNavigate = (page) => {
    console.log(`Navigate to ${page}`);
  };

  // This function was replaced by the new handleNavigateToPendingCustomers above

  // Widget click handler
  const handleWidgetClick = (type) => {
    console.log(`Widget clicked: ${type}`);
  };

  // Fetch dashboard data function
  const fetchDashboardData = () => {
    fetchDashboardCounts();
    fetchMonthlySignupData();
  };

  // Color scheme for charts
  const CHART_COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00ff00', '#ff0000'];

  // Render trend indicator
  const renderTrend = (trend) => {
    const Icon = trend.direction === 'up' ? ArrowUpOutlined : ArrowDownOutlined;
    const color = trend.direction === 'up' ? '#52c41a' : '#ff4d4f';
    
    return (
      <Space size="small">
        <Icon style={{ color, fontSize: '12px' }} />
        <Text style={{ color, fontSize: '12px', fontWeight: 500 }}>
          {Math.abs(trend.change)}%
        </Text>
      </Space>
    );
  };


  // Solution Partner Analytics Cards
  const partnerWidgets = [
    {
      title: 'Total Solution Partners',
      value: dashboardData.totalSolutionPartners,
      icon: <CrownOutlined style={{ color: '#eb2f96' }} />,
      color: '#eb2f96',
      trend: dashboardData.trends.partners,
      description: `${dashboardData.activeSolutionPartners} active partners`,
      onClick: () => handleWidgetClick('partners')
    },
    {
      title: 'Partner Generated Revenue',
      value: `$${dashboardData.partnerGeneratedRevenue.toLocaleString()}`,
      icon: <DollarOutlined style={{ color: '#13c2c2' }} />,
      color: '#13c2c2',
      trend: { change: 22.4, direction: 'up' },
      description: 'Revenue from partner customers',
      onClick: () => handleWidgetClick('partnerRevenue')
    },
    {
      title: 'Avg Customers per Partner',
      value: Math.round((dashboardData.totalCustomers * 0.3) / dashboardData.totalSolutionPartners),
      icon: <TeamOutlined style={{ color: '#f759ab' }} />,
      color: '#f759ab',
      trend: { change: 5.3, direction: 'up' },
      description: 'Customer conversion efficiency',
      onClick: () => handleWidgetClick('partnerEfficiency')
    },
    {
      title: 'New Partners This Month',
      value: dashboardData.newPartnersThisMonth,
      icon: <ShopOutlined style={{ color: '#95de64' }} />,
      color: '#95de64',
      trend: { change: 12.5, direction: 'up' },
      description: 'Partner acquisition growth',
      onClick: () => handleWidgetClick('newPartners')
    }
  ];

  // Revenue & Billing Analytics Cards
  const revenueWidgets = [
    {
      title: 'Total Revenue',
      value: `$${dashboardData.totalRevenue.toLocaleString()}`,
      icon: <DollarOutlined style={{ color: '#52c41a' }} />,
      color: '#52c41a',
      trend: dashboardData.trends.revenue,
      description: 'All-time revenue generated',
      onClick: () => handleWidgetClick('revenue')
    },
    {
      title: 'Monthly Recurring Revenue',
      value: `$${dashboardData.monthlyRecurringRevenue.toLocaleString()}`,
      icon: <BankOutlined style={{ color: '#1890ff' }} />,
      color: '#1890ff',
      trend: { change: 18.3, direction: 'up' },
      description: 'Predictable monthly income',
      onClick: () => handleWidgetClick('mrr')
    },
    {
      title: 'Average Revenue Per Customer',
      value: `$${dashboardData.averageRevenuePerCustomer.toFixed(2)}`,
      icon: <ShoppingCartOutlined style={{ color: '#722ed1' }} />,
      color: '#722ed1',
      trend: { change: 7.9, direction: 'up' },
      description: 'Customer lifetime value indicator',
      onClick: () => handleWidgetClick('arpc')
    },
    {
      title: 'Outstanding Payments',
      value: `$${dashboardData.outstandingPayments.toLocaleString()}`,
      icon: <ClockCircleOutlined style={{ color: '#fa541c' }} />,
      color: '#fa541c',
      trend: { change: -15.2, direction: 'down' },
      description: 'Pending payment collections',
      onClick: () => handleWidgetClick('outstanding')
    }
  ];

  // WhatsApp API Usage Cards
  const apiWidgets = [
    {
      title: 'Total Messages Processed',
      value: dashboardData.totalMessagesProcessed.toLocaleString(),
      icon: <MessageOutlined style={{ color: '#25D366' }} />,
      color: '#25D366',
      trend: dashboardData.trends.messages,
      description: 'All-time message count',
      onClick: () => handleWidgetClick('messages')
    },
    {
      title: 'Monthly Messages',
      value: dashboardData.monthlyMessages.toLocaleString(),
      icon: <SendOutlined style={{ color: '#1890ff' }} />,
      color: '#1890ff',
      trend: { change: 23.5, direction: 'up' },
      description: 'Current month activity',
      onClick: () => handleWidgetClick('monthlyMessages')
    },
    {
      title: 'API Calls This Month',
      value: dashboardData.apiCallsThisMonth.toLocaleString(),
      icon: <ApiOutlined style={{ color: '#722ed1' }} />,
      color: '#722ed1',
      trend: dashboardData.trends.apiUsage,
      description: 'Total API endpoint calls',
      onClick: () => handleWidgetClick('apiUsage')
    },
    {
      title: 'Daily Average Messages',
      value: dashboardData.dailyMessages.toLocaleString(),
      icon: <BarChartOutlined style={{ color: '#fa8c16' }} />,
      color: '#fa8c16',
      trend: { change: 12.8, direction: 'up' },
      description: 'Average daily throughput',
      onClick: () => handleWidgetClick('dailyMessages')
    }
  ];

  // System Performance Cards
  const systemWidgets = [
    {
      title: 'System Uptime',
      value: `${dashboardData.systemUptime}%`,
      icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      color: '#52c41a',
      trend: dashboardData.trends.systemHealth,
      description: 'Service availability this month',
      onClick: () => handleWidgetClick('uptime')
    },
    {
      title: 'API Response Time',
      value: `${dashboardData.apiResponseTime}ms`,
      icon: <ClockCircleOutlined style={{ color: '#1890ff' }} />,
      color: '#1890ff',
      trend: { change: -8.5, direction: 'down' },
      description: 'Average response time',
      onClick: () => handleWidgetClick('responseTime')
    },
    {
      title: 'Error Rate',
      value: `${dashboardData.errorRate}%`,
      icon: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
      color: '#ff4d4f',
      trend: { change: -12.3, direction: 'down' },
      description: 'System error percentage',
      onClick: () => handleWidgetClick('errorRate')
    },
    {
      title: 'Active Connections',
      value: dashboardData.activeConnections.toLocaleString(),
      icon: <GlobalOutlined style={{ color: '#13c2c2' }} />,
      color: '#13c2c2',
      trend: { change: 15.7, direction: 'up' },
      description: 'Real-time active users',
      onClick: () => handleWidgetClick('connections')
    }
  ];

  // Compact widget component with inline count
  const renderUpdatedWidget = (widget, index) => {
    const isSelected = selectedView === widget.id;
    
    return (
      <Col xs={24} sm={12} lg={6} key={index} style={{ display: 'flex' }}>
        <div 
          onClick={widget.onClick}
          className={`admin-stat-card clickable admin-animate-in ${isSelected ? 'selected' : ''}`}
          style={{ 
            borderColor: isSelected ? widget.color : undefined,
            boxShadow: isSelected ? `0 0 0 3px ${widget.color}18, 0 4px 16px ${widget.color}12` : undefined,
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column'
          }}
        >
          {/* Top indicator bar when selected */}
          {isSelected && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              background: widget.color,
              borderRadius: '12px 12px 0 0'
            }} />
          )}
          
          <div 
            className="admin-stat-icon"
            style={{
              background: isSelected ? widget.color : `${widget.color}12`,
              color: isSelected ? '#fff' : widget.color,
            }}
          >
            {widget.icon}
          </div>
          
          <div className="admin-stat-content" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div className="admin-stat-label">{widget.title}</div>
            <div className="admin-stat-value" style={{ color: widget.color }}>
              {widget.isStatusCard ? (
                <span>
                  <span style={{ color: '#10b981' }}>{widget.activeCount}</span>
                  <span style={{ color: '#d1d5db', margin: '0 4px', fontWeight: 400 }}>/</span>
                  <span style={{ color: '#ef4444' }}>{widget.pendingCount}</span>
                </span>
              ) : (
                typeof widget.value === 'number' ? widget.value.toLocaleString() : widget.value
              )}
            </div>
            <div className="admin-stat-desc">{widget.description}</div>
          </div>
        </div>
      </Col>
    );
  };

  // Modal content based on type
  const getModalContent = () => {
    const { type, data } = detailModal;
    
    const columns = {
      customers: [
        { title: 'Company', dataIndex: 'name', key: 'name' },
        { title: 'Email', dataIndex: 'email', key: 'email' },
        { title: 'Status', dataIndex: 'status', key: 'status', render: (status) => (
          <Tag color={status === 'Active' ? 'green' : 'red'}>{status}</Tag>
        )},
        { title: 'Join Date', dataIndex: 'joinDate', key: 'joinDate' },
        { title: 'Messages/Month', dataIndex: 'messagesThisMonth', key: 'messagesThisMonth' },
        { title: 'Revenue', dataIndex: 'revenue', key: 'revenue', render: (revenue) => `$${revenue}` },
        { title: 'Plan', dataIndex: 'plan', key: 'plan' }
      ],
      partners: [
        { title: 'Partner Name', dataIndex: 'name', key: 'name' },
        { title: 'Email', dataIndex: 'email', key: 'email' },
        { title: 'Status', dataIndex: 'status', key: 'status', render: (status) => (
          <Tag color={status === 'Active' ? 'green' : 'red'}>{status}</Tag>
        )},
        { title: 'Join Date', dataIndex: 'joinDate', key: 'joinDate' },
        { title: 'Customers Referred', dataIndex: 'customersReferred', key: 'customersReferred' },
        { title: 'Total Revenue', dataIndex: 'totalRevenue', key: 'totalRevenue', render: (revenue) => `$${revenue}` },
        { title: 'Commission', dataIndex: 'commission', key: 'commission', render: (commission) => `$${commission}` }
      ],
      revenue: [
        { title: 'Month', dataIndex: 'month', key: 'month' },
        { title: 'Direct Customers', dataIndex: 'directCustomers', key: 'directCustomers', render: (revenue) => `$${revenue}` },
        { title: 'Partner Revenue', dataIndex: 'partnerRevenue', key: 'partnerRevenue', render: (revenue) => `$${revenue}` },
        { title: 'Total Revenue', dataIndex: 'totalRevenue', key: 'totalRevenue', render: (revenue) => `$${revenue}` },
        { title: 'Growth', dataIndex: 'growth', key: 'growth' }
      ],
      messages: [
        { title: 'Date', dataIndex: 'date', key: 'date' },
        { title: 'Total Messages', dataIndex: 'totalMessages', key: 'totalMessages' },
        { title: 'Successful', dataIndex: 'successfulDeliveries', key: 'successfulDeliveries' },
        { title: 'Failed', dataIndex: 'failedMessages', key: 'failedMessages' },
        { title: 'Cost', dataIndex: 'cost', key: 'cost', render: (cost) => `$${cost}` },
        { title: 'Customers', dataIndex: 'customers', key: 'customers' }
      ],
      apiUsage: [
        { title: 'Endpoint', dataIndex: 'endpoint', key: 'endpoint' },
        { title: 'Total Calls', dataIndex: 'calls', key: 'calls' },
        { title: 'Success Rate', dataIndex: 'successRate', key: 'successRate' },
        { title: 'Avg Response Time', dataIndex: 'avgResponseTime', key: 'avgResponseTime' },
        { title: 'Errors', dataIndex: 'errors', key: 'errors' }
      ]
    };

    return (
      <Table
        dataSource={data}
        columns={columns[type] || []}
        pagination={{ pageSize: 10 }}
        size="small"
        rowKey="id"
      />
    );
  };

  return (
    <div className="admin-page">
      <Spin spinning={loading}>
        {/* Page Header */}
        <div className="admin-dashboard-header">
          <div className="admin-dashboard-header-top">
            <div>
              <h1 className="admin-dashboard-header-title">Admin Dashboard</h1>
              <p className="admin-dashboard-header-subtitle">Monitor platform health, customers & partners</p>
            </div>
            <div className="admin-dashboard-header-controls">
              <div className="admin-dashboard-header-controls-left">
                <span className="admin-controls-label">Period</span>
                <Select
                  value={timeRange}
                  onChange={setTimeRange}
                  style={{ width: 140 }}
                  size="middle"
                  variant="borderless"
                  className="admin-period-select"
                >
                  <Option value="7d">Last 7 days</Option>
                  <Option value="30d">Last 30 days</Option>
                  <Option value="90d">Last 90 days</Option>
                  <Option value="1y">Last year</Option>
                </Select>
              </div>
              <div className="admin-dashboard-header-controls-right">
                <span className="admin-controls-label">Range</span>
                <RangePicker
                  value={dateRange}
                  onChange={setDateRange}
                  format="YYYY-MM-DD"
                  size="middle"
                  variant="borderless"
                  className="admin-range-input"
                  popupClassName="admin-range-picker-popup"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Monthly Customer Signup Chart — Premium Area Chart */}
        {(() => {
          const totalSignups = monthlyChartData.reduce((sum, d) => sum + d.totalCustomers, 0);
          const totalDirect = monthlyChartData.reduce((sum, d) => sum + d.directCustomers, 0);
          const totalPartners = monthlyChartData.reduce((sum, d) => sum + d.solutionPartners, 0);
          const bestMonth = monthlyChartData.reduce((best, d) => d.totalCustomers > (best?.totalCustomers || 0) ? d : best, monthlyChartData[0]);
          const prevMonth = monthlyChartData.length >= 2 ? monthlyChartData[monthlyChartData.length - 2] : null;
          const latestMonth = monthlyChartData.length >= 1 ? monthlyChartData[monthlyChartData.length - 1] : null;
          const growthPct = prevMonth && prevMonth.totalCustomers > 0
            ? Math.round(((latestMonth.totalCustomers - prevMonth.totalCustomers) / prevMonth.totalCustomers) * 100)
            : latestMonth?.totalCustomers > 0 ? 100 : 0;
          const isGrowthPositive = growthPct >= 0;

          const CustomTooltip = ({ active, payload, label }) => {
            if (!active || !payload?.length) return null;
            return (
              <div style={{
                background: 'rgba(15, 23, 42, 0.95)',
                backdropFilter: 'blur(12px)',
                borderRadius: 12,
                padding: '14px 18px',
                border: '1px solid rgba(255,255,255,0.1)',
                boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                minWidth: 180,
              }}>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>{label}</div>
                {payload.map((entry, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: idx < payload.length - 1 ? 6 : 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: entry.color, boxShadow: `0 0 8px ${entry.color}60` }} />
                      <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>{entry.name}</span>
                    </div>
                    <span style={{ fontSize: 14, color: '#fff', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{entry.value}</span>
                  </div>
                ))}
              </div>
            );
          };

          return (
            <div style={{
              background: '#fff',
              borderRadius: 20,
              border: '1px solid #e8ecf1',
              overflow: 'hidden',
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            }}>
              {/* Header */}
              <div style={{ padding: '20px 28px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 12,
                    background: '#0f172a',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <RiseOutlined style={{ color: '#fff', fontSize: 18 }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.02em' }}>Customer Growth</div>
                    <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 450, marginTop: 1 }}>Monthly signups across all channels</div>
                  </div>
                </div>

                {/* Inline compact stats + growth badge */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
                  {[
                    { label: 'Signups', value: totalSignups, color: '#6366f1' },
                    { label: 'Direct', value: totalDirect, color: '#10b981' },
                    { label: 'Partners', value: totalPartners, color: '#f59e0b' },
                  ].map(s => (
                    <div key={s.label} style={{ textAlign: 'center', minWidth: 50 }}>
                      <div style={{ fontSize: 18, fontWeight: 800, color: s.color, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{s.value.toLocaleString()}</div>
                      <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 2 }}>{s.label}</div>
                    </div>
                  ))}
                  {latestMonth && (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      background: isGrowthPositive ? '#ecfdf5' : '#fef2f2',
                      borderRadius: 8, padding: '6px 10px',
                      border: `1px solid ${isGrowthPositive ? '#10b98120' : '#ef444420'}`,
                    }}>
                      {isGrowthPositive
                        ? <ArrowUpOutlined style={{ color: '#10b981', fontSize: 12 }} />
                        : <ArrowDownOutlined style={{ color: '#ef4444', fontSize: 12 }} />
                      }
                      <span style={{ fontSize: 13, fontWeight: 700, color: isGrowthPositive ? '#10b981' : '#ef4444', fontVariantNumeric: 'tabular-nums' }}>
                        {isGrowthPositive ? '+' : ''}{growthPct}%
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Chart area */}
              <div style={{ padding: '12px 20px 20px' }}>
                {monthlyChartData.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
                    <BarChartOutlined style={{ fontSize: 40, marginBottom: 12, opacity: 0.4 }} />
                    <div style={{ fontSize: 14, fontWeight: 500 }}>No monthly data available</div>
                  </div>
                ) : (
                  <div style={{
                    background: 'linear-gradient(180deg, #f8faff 0%, #ffffff 100%)',
                    borderRadius: 16,
                    padding: '20px 12px 8px',
                    border: '1px solid #f0f2f8',
                  }}>
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={monthlyChartData} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
                        <defs>
                          <linearGradient id="gradTotal" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#6366f1" stopOpacity={0.35} />
                            <stop offset="100%" stopColor="#6366f1" stopOpacity={0.02} />
                          </linearGradient>
                          <linearGradient id="gradDirect" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
                            <stop offset="100%" stopColor="#10b981" stopOpacity={0.02} />
                          </linearGradient>
                          <linearGradient id="gradPartners" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.3} />
                            <stop offset="100%" stopColor="#f59e0b" stopOpacity={0.02} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e8ecf1" vertical={false} />
                        <XAxis
                          dataKey="month"
                          tick={{ fontSize: 12, fill: '#94a3b8', fontWeight: 500 }}
                          tickLine={false}
                          axisLine={{ stroke: '#e8ecf1' }}
                        />
                        <YAxis
                          tick={{ fontSize: 12, fill: '#94a3b8', fontWeight: 500 }}
                          tickLine={false}
                          axisLine={false}
                          allowDecimals={false}
                        />
                        <RechartsTooltip content={<CustomTooltip />} cursor={{ stroke: '#6366f120', strokeWidth: 2 }} />
                        <Area
                          type="monotone"
                          dataKey="totalCustomers"
                          name="Total"
                          stroke="#6366f1"
                          strokeWidth={3}
                          fill="url(#gradTotal)"
                          dot={{ r: 4, fill: '#6366f1', strokeWidth: 2, stroke: '#fff' }}
                          activeDot={{ r: 6, fill: '#6366f1', strokeWidth: 3, stroke: '#fff', style: { filter: 'drop-shadow(0 0 6px rgba(99,102,241,0.5))' } }}
                        />
                        <Area
                          type="monotone"
                          dataKey="directCustomers"
                          name="Direct"
                          stroke="#10b981"
                          strokeWidth={2.5}
                          fill="url(#gradDirect)"
                          dot={{ r: 3, fill: '#10b981', strokeWidth: 2, stroke: '#fff' }}
                          activeDot={{ r: 5, fill: '#10b981', strokeWidth: 3, stroke: '#fff', style: { filter: 'drop-shadow(0 0 6px rgba(16,185,129,0.5))' } }}
                        />
                        <Area
                          type="monotone"
                          dataKey="solutionPartners"
                          name="Partners"
                          stroke="#f59e0b"
                          strokeWidth={2}
                          fill="url(#gradPartners)"
                          dot={{ r: 3, fill: '#f59e0b', strokeWidth: 2, stroke: '#fff' }}
                          activeDot={{ r: 5, fill: '#f59e0b', strokeWidth: 3, stroke: '#fff', style: { filter: 'drop-shadow(0 0 6px rgba(245,158,11,0.5))' } }}
                        />
                        <Legend
                          verticalAlign="top"
                          align="right"
                          iconType="circle"
                          iconSize={8}
                          wrapperStyle={{ fontSize: 12, fontWeight: 500, paddingBottom: 12 }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>
          );
        })()}


        {/* Dashboard Tabs */}
        <Tabs defaultActiveKey={adminDefaultTab} type="line" size="middle" className="admin-tabs">
          {hasAdminTabAccess('admin') && (
          <TabPane tab="Overview" key="overview">
            {/* Customer Analytics */}
            <div className="admin-section">
              <div className="admin-section-title">
                <TeamOutlined /> Customer Analytics
              </div>
              <Row gutter={[12, 12]}>
                {customerWidgets.map(renderUpdatedWidget)}
              </Row>
            </div>

            {/* Inline Table View */}
            <div className="admin-table-wrapper">
              <div className="admin-table-header">
                <span className="admin-table-title">
                  {`${selectedView.charAt(0).toUpperCase() + selectedView.slice(1)} Customers`}
                </span>
                <span className="admin-table-badge">
                  {tableData.length} records
                </span>
              </div>
              <Table
                dataSource={tableData}
                columns={getTableColumns()}
                loading={tableLoading}
                pagination={{ 
                  pageSize: 10,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
                }}
                size="small"
                rowKey="companyid"
                scroll={{ x: 900 }}
              />
            </div>
          </TabPane>
          )}
          
          {/* Partner Applications Management */}
          {/* <TabPane tab="Partner Applications" key="partner-applications">
            <Card 
              title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Solution Partner Applications</span>
                  <Space>
                    <Badge 
                      count={partnerApplications.filter(app => app.status === 'pending_review').length} 
                      style={{ backgroundColor: '#fa8c16' }}
                    />
                    <Button type="primary" size="small" onClick={() => {
                      fetchPartnerApplications();
                      message.success('Partner applications refreshed');
                    }}>
                      Refresh
                    </Button>
                  </Space>
                </div>
              }
              bordered={false}
              size="small"
              bodyStyle={{ padding: '12px' }}
            >
              <Table
                dataSource={partnerApplications}
                columns={[
                  {
                    title: 'Partner Name',
                    dataIndex: 'partnerName',
                    key: 'partnerName',
                    render: (text, record) => (
                      <div>
                        <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{text}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          <MailOutlined style={{ marginRight: 4 }} />
                          {record.supportEmail}
                        </div>
                      </div>
                    ),
                  },
                  {
                    title: 'Brand Color',
                    dataIndex: 'primaryColor',
                    key: 'primaryColor',
                    render: (color) => (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div
                          style={{
                            width: 20,
                            height: 20,
                            backgroundColor: color,
                            borderRadius: 4,
                            border: '1px solid #d9d9d9',
                          }}
                        />
                        <Text style={{ fontSize: '12px' }}>{color}</Text>
                      </div>
                    ),
                  },
                  {
                    title: 'Application Date',
                    dataIndex: 'applicationDate',
                    key: 'applicationDate',
                    render: (date) => (
                      <Text style={{ fontSize: '12px' }}>
                        {new Date(date).toLocaleDateString()}
                      </Text>
                    ),
                  },
                  {
                    title: 'Status',
                    dataIndex: 'status',
                    key: 'status',
                    render: (status, record) => {
                      const statusConfig = {
                        pending_review: { color: 'orange', text: 'Pending Review' },
                        approved: { color: 'green', text: 'Approved' },
                        rejected: { color: 'red', text: 'Rejected' },
                      };
                      const config = statusConfig[status] || { color: 'default', text: status };
                      
                      return (
                        <div>
                          <Tag color={config.color}>{config.text}</Tag>
                          {status === 'rejected' && record.rejectionReason && (
                            <div style={{ fontSize: '11px', color: '#ff4d4f', marginTop: 2 }}>
                              {record.rejectionReason}
                            </div>
                          )}
                        </div>
                      );
                    },
                  },
                  {
                    title: 'SMTP Config',
                    key: 'smtpConfig',
                    render: (_, record) => (
                      <div style={{ fontSize: '11px' }}>
                        <div><strong>Host:</strong> {record.businessConfig.smtp_host}</div>
                        <div><strong>Port:</strong> {record.businessConfig.port}</div>
                        <div><strong>SSL:</strong> {record.businessConfig.ssl_enabled ? 'Yes' : 'No'}</div>
                      </div>
                    ),
                  },
                  {
                    title: 'Priority',
                    dataIndex: 'priority',
                    key: 'priority',
                    render: (priority) => {
                      const priorityConfig = {
                        high: { color: 'red', text: 'High' },
                        medium: { color: 'orange', text: 'Medium' },
                        low: { color: 'blue', text: 'Low' },
                      };
                      const config = priorityConfig[priority] || { color: 'default', text: priority };
                      
                      return <Tag color={config.color}>{config.text}</Tag>;
                    },
                  },
                  {
                    title: 'Actions',
                    key: 'actions',
                    render: (_, record) => (
                      <Space size="small">
                        <Tooltip title="View Details">
                          <Button
                            type="text"
                            icon={<EyeOutlined />}
                            size="small"
                            onClick={() => {
                              Modal.info({
                                title: 'Partner Application Details',
                                width: 600,
                                content: (
                                  <div style={{ marginTop: 16 }}>
                                    <Row gutter={[16, 16]}>
                                      <Col span={12}>
                                        <div><strong>Partner Name:</strong> {record.partnerName}</div>
                                      </Col>
                                      <Col span={12}>
                                        <div><strong>Support Email:</strong> {record.supportEmail}</div>
                                      </Col>
                                      <Col span={12}>
                                        <div><strong>Application Date:</strong> {record.applicationDate}</div>
                                      </Col>
                                      <Col span={12}>
                                        <div><strong>Status:</strong> {record.status}</div>
                                      </Col>
                                      <Col span={24}>
                                        <div><strong>Business Configuration:</strong></div>
                                        <div style={{ marginLeft: 16, marginTop: 8 }}>
                                          <div>SMTP Host: {record.businessConfig.smtp_host}</div>
                                          <div>Port: {record.businessConfig.port}</div>
                                          <div>SSL Enabled: {record.businessConfig.ssl_enabled ? 'Yes' : 'No'}</div>
                                        </div>
                                      </Col>
                                      {record.rejectionReason && (
                                        <Col span={24}>
                                          <div><strong>Rejection Reason:</strong></div>
                                          <div style={{ color: '#ff4d4f', marginTop: 4 }}>{record.rejectionReason}</div>
                                        </Col>
                                      )}
                                    </Row>
                                  </div>
                                ),
                              });
                            }}
                          />
                        </Tooltip>
                        {record.status === 'pending_review' && (
                          <>
                            <Tooltip title="Approve Application">
                              <Button
                                type="text"
                                icon={<CheckCircleOutlined />}
                                size="small"
                                style={{ color: '#52c41a' }}
                                onClick={() => {
                                  Modal.confirm({
                                    title: 'Approve Partner Application',
                                    content: `Are you sure you want to approve ${record.partnerName}'s application?`,
                                    onOk: () => handleApproveApplication(record.id, record.partnerName),
                                  });
                                }}
                              />
                            </Tooltip>
                            <Tooltip title="Reject Application">
                              <Button
                                type="text"
                                icon={<CloseCircleOutlined />}
                                size="small"
                                style={{ color: '#ff4d4f' }}
                                onClick={() => {
                                  Modal.confirm({
                                    title: 'Reject Partner Application',
                                    content: (
                                      <div>
                                        <p>Are you sure you want to reject {record.partnerName}'s application?</p>
                                        <Input.TextArea
                                          placeholder="Please provide a reason for rejection..."
                                          rows={3}
                                          id="rejection-reason"
                                        />
                                      </div>
                                    ),
                                    onOk: () => {
                                      const reason = document.getElementById('rejection-reason')?.value || 'No reason provided';
                                      handleRejectApplication(record.id, record.partnerName, reason);
                                    },
                                  });
                                }}
                              />
                            </Tooltip>
                          </>
                        )}
                      </Space>
                    ),
                  },
                ]}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} applications`,
                }}
                size="small"
                rowKey="id"
                scroll={{ x: 1200 }}
              />
            </Card>
          </TabPane> */}
          {hasAdminTabAccess('platform_plans') && (
          <TabPane 
            tab={
              <span>
                <CrownOutlined style={{ marginRight: 8 }} />
                Subscription Plans
              </span>
            } 
            key="subscription-plans"
          >
            <SubscriptionPlansPage />
          </TabPane>
          )}
          {/* Topup Plans tab hidden — plan-based billing replaces topups
          <TabPane 
            tab={
              <span>
                <ShoppingCartOutlined style={{ marginRight: 8 }} />
                Topup Plans
              </span>
            } 
            key="topup-plans"
          >
            <TopupPlansPage />
          </TabPane>
          */}
          {hasAdminTabAccess('admin') && (
          <TabPane 
            tab={
              <span>
                <TeamOutlined style={{ marginRight: 8 }} />
                Admin Management
              </span>
            } 
            key="admin-management"
          >
            <AdminUserManagement />
          </TabPane>
          )}
          {hasAdminTabAccess('payment_management') && (
          <TabPane 
            tab={
              <span>
                <BankOutlined style={{ marginRight: 8 }} />
                Payment Management
              </span>
            } 
            key="payment-management"
          >
            <PaymentManagement />
          </TabPane>
          )}
          {hasAdminTabAccess('admin') && (
          <TabPane 
            tab={
              <span>
                <SafetyOutlined style={{ marginRight: 8 }} />
                Audit Logs
              </span>
            } 
            key="audit-logs"
          >
            <AuditLogs />
          </TabPane>
          )}
          {/* <TabPane 
            tab={
              <span>
                API & Performance
                <Badge 
                  count="Coming Soon" 
                  style={{ 
                    backgroundColor: '#faad14', 
                    marginLeft: 8,
                    fontSize: '10px',
                    height: '18px',
                    lineHeight: '18px'
                  }} 
                />
              </span>
            } 
            key="api-performance" 
            disabled
          >
            <Alert
              message="Coming Soon"
              description="API & Performance analytics will be available in the next update."
              type="info"
              showIcon
            />
          </TabPane> */}

         

        </Tabs>

        {/* Referred Companies Modal */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <TeamOutlined style={{ color: '#1890ff' }} />
              <span>Companies Referred by {referredCompaniesModal.partnerName}</span>
            </div>
          }
          open={referredCompaniesModal.visible}
          onCancel={() => setReferredCompaniesModal({ visible: false, data: [], loading: false, partnerName: '' })}
          width={1200}
          footer={[
            <Button 
              key="close" 
              onClick={() => setReferredCompaniesModal({ visible: false, data: [], loading: false, partnerName: '' })}
            >
              Close
            </Button>
          ]}
        >
          <Spin spinning={referredCompaniesModal.loading}>
            {referredCompaniesModal.data.length > 0 ? (
              <>
                <div style={{ marginBottom: 16 }}>
                  <Text type="secondary">
                    Showing {referredCompaniesModal.data.length} companies referred by this partner
                  </Text>
                </div>
                <Table
                  dataSource={referredCompaniesModal.data}
                  columns={[
                    {
                      title: 'Sr. No.',
                      key: 'srno',
                      width: 70,
                      render: (_, record, index) => (
                        <div style={{ textAlign: 'center', fontWeight: 500 }}>
                          {index + 1}
                        </div>
                      ),
                    },
                    {
                      title: 'Company Details',
                      key: 'company',
                      render: (_, record) => (
                        <div>
                          <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: 4 }}>
                            {record.companyname || 'Unknown Company'}
                          </div>
                          <div style={{ fontSize: '12px', color: '#666', lineHeight: '18px' }}>
                            <div><MailOutlined /> {record.emailid || 'N/A'}</div>
                            <div><PhoneOutlined /> {record.contactno ? `${record.countrycode || ''}${record.contactno}` : 'N/A'}</div>
                          </div>
                        </div>
                      ),
                    },
                    {
                      title: 'Status',
                      key: 'status',
                      render: (_, record) => (
                        <Tag color={record.status === 1 ? 'green' : 'orange'}>
                          {record.status === 1 ? 'Active' : 'Pending'}
                        </Tag>
                      ),
                    },
                    {
                      title: 'Verification',
                      key: 'verification',
                      render: (_, record) => (
                        <Tag color={record.isverified === 1 ? 'blue' : 'orange'}>
                          {record.isverified === 1 ? 'Verified' : 'Unverified'}
                        </Tag>
                      ),
                    },
                    {
                      title: 'Join Date',
                      key: 'joinDate',
                      render: (_, record) => (
                        <div style={{ fontSize: '12px' }}>
                          {moment(record.entrytime).format('MMM DD, YYYY')}
                        </div>
                      ),
                    },
                    {
                      title: 'Revenue Potential',
                      key: 'revenue',
                      render: (_, record) => (
                        <div style={{ fontSize: '12px' }}>
                          <div style={{ color: '#52c41a', fontWeight: 'bold' }}>
                            ${((record.activecustomer || 0) * 25).toFixed(2)}/month
                          </div>
                          <div style={{ color: '#666' }}>
                            Commission: ${((record.activecustomer || 0) * 2.5).toFixed(2)}
                          </div>
                        </div>
                      ),
                    }
                  ]}
                  rowKey="companyid"
                  size="small"
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} companies`
                  }}
                  scroll={{ x: 800 }}
                />
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <Text type="secondary">No companies found for this partner</Text>
              </div>
            )}
          </Spin>
        </Modal>

      </Spin>

      {/* ── Company Detail Drawer ─────────────────────────────────────── */}
      <Drawer
        zIndex={998}
        style={{ marginTop: 56, height: 'calc(100% - 56px)' }}
        maskStyle={{ marginTop: 56 }}
        bodyStyle={{ overflowY: 'auto', paddingBottom: 32 }}
        title={
          <Space>
            <ShopOutlined />
            <span>Company Details</span>
            {companyDetailDrawer.company?.companyname && (
              <Text strong style={{ fontSize: '14px' }}>
                — {companyDetailDrawer.company.companyname}
              </Text>
            )}
          </Space>
        }
        width={Math.min(520, window.innerWidth)}
        placement="right"
        onClose={closeCompanyDetail}
        open={companyDetailDrawer.visible}
        destroyOnClose
      >
        {companyDetailDrawer.company && (
          <Space direction="vertical" style={{ width: '100%' }} size="large">

            {/* Basic Info */}
            <Card size="small" title="Company Information">
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label="Company Name">
                  {companyDetailDrawer.company.companyname || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Email">
                  {companyDetailDrawer.company.emailid || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Phone">
                  {companyDetailDrawer.company.contactno
                    ? `${companyDetailDrawer.company.countrycode || ''}${companyDetailDrawer.company.contactno}`
                    : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Type">
                  <Tag color={companyDetailDrawer.company.company_type === 'SOLUTION_PARTNER' ? 'purple' : 'blue'}>
                    {companyDetailDrawer.company.company_type === 'SOLUTION_PARTNER' ? 'Solution Partner' : 'Direct Customer'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Account Status">
                  <Tag color={companyDetailDrawer.company.status === 1 ? 'green' : 'orange'}>
                    {companyDetailDrawer.company.status === 1 ? 'Active' : 'Pending'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Verified">
                  <Tag color={companyDetailDrawer.company.isverified === 1 ? 'blue' : 'orange'}>
                    {companyDetailDrawer.company.isverified === 1 ? 'Verified' : 'Unverified'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Joined">
                  {companyDetailDrawer.company.entrytime
                    ? moment(companyDetailDrawer.company.entrytime).format('MMM DD, YYYY')
                    : '—'}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* WABA Status Section */}
            <Card
              size="small"
              title={
                <Space>
                  <WhatsAppOutlined style={{ color: '#25D366' }} />
                  <span>WABA Status</span>
                </Space>
              }
              extra={
                <Space size={4}>
                  <Tooltip title="Subscribe WABA Webhooks">
                    <Button
                      type="text"
                      size="small"
                      icon={<WhatsAppOutlined />}
                      loading={subscribingWebhooks}
                      style={{ color: '#25D366' }}
                      onClick={() => confirmSubscribeWabaWebhooks(companyDetailDrawer.company.companyid)}
                    />
                  </Tooltip>
                  <Tooltip title="Refresh WABA status">
                    <Button
                      type="text"
                      size="small"
                      icon={<ReloadOutlined />}
                      loading={loadingWaba}
                      onClick={() => fetchCompanyWabaDetails(companyDetailDrawer.company.customeruuid)}
                    />
                  </Tooltip>
                </Space>
              }
            >
              <Spin spinning={loadingWaba}>
                {wabaDetail ? (
                  <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    {/* Status Badge */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <Text strong>Current Status:</Text>
                      {wabaDetail.waba_status === 'active' && (
                        <Badge status="success" text={<Tag color="green">Active</Tag>} />
                      )}
                      {wabaDetail.waba_status === 'pending' && (
                        <Badge status="warning" text={<Tag color="orange">Pending</Tag>} />
                      )}
                      {(wabaDetail.waba_status === 'inactive' || (!wabaDetail.waba_status)) && (
                        <Badge status="error" text={<Tag color="red">{wabaDetail.waba_status || 'Inactive'}</Tag>} />
                      )}
                      {wabaDetail.waba_status && !['active', 'pending', 'inactive'].includes(wabaDetail.waba_status) && (
                        <Badge status="default" text={<Tag>{wabaDetail.waba_status}</Tag>} />
                      )}
                    </div>

                    {/* WABA Info */}
                    {wabaDetail.phone_number_id && (
                      <Descriptions column={1} size="small" bordered>
                        {wabaDetail.phone_number_id && (
                          <Descriptions.Item label="Phone Number ID">
                            <Text copyable code style={{ fontSize: '12px' }}>{wabaDetail.phone_number_id}</Text>
                          </Descriptions.Item>
                        )}
                        {wabaDetail.waba_id && (
                          <Descriptions.Item label="WABA ID">
                            <Text copyable code style={{ fontSize: '12px' }}>{wabaDetail.waba_id}</Text>
                          </Descriptions.Item>
                        )}
                        {wabaDetail.verified_name && (
                          <Descriptions.Item label="Verified Name">
                            {wabaDetail.verified_name}
                          </Descriptions.Item>
                        )}
                        {wabaDetail.quality_rating && (
                          <Descriptions.Item label="Quality Rating">
                            <Tag color={wabaDetail.quality_rating === 'GREEN' ? 'green' : wabaDetail.quality_rating === 'YELLOW' ? 'orange' : 'red'}>
                              {wabaDetail.quality_rating}
                            </Tag>
                          </Descriptions.Item>
                        )}
                      </Descriptions>
                    )}

                    <Divider style={{ margin: '8px 0' }} />

                    {/* Action Buttons */}
                    <Space wrap>
                      {wabaDetail.waba_status !== 'active' && (
                        <Button
                          type="primary"
                          icon={<WhatsAppOutlined />}
                          loading={updatingWabaStatus}
                          style={{ backgroundColor: '#25D366', borderColor: '#25D366' }}
                          onClick={() => confirmActivateWaba(companyDetailDrawer.company.companyid)}
                        >
                          Activate WABA
                        </Button>
                      )}
                      <Select
                        size="small"
                        placeholder="Change status…"
                        style={{ width: 160 }}
                        disabled={updatingWabaStatus}
                        onChange={(val) => {
                          if (val === 'active') {
                            confirmActivateWaba(companyDetailDrawer.company.companyid);
                          } else {
                            confirmChangeWabaStatus(companyDetailDrawer.company.companyid, val);
                          }
                        }}
                      >
                        <Select.Option value="active" disabled={wabaDetail.waba_status === 'active'}>Set Active</Select.Option>
                        <Select.Option value="pending" disabled={wabaDetail.waba_status === 'pending'}>Set Pending</Select.Option>
                        <Select.Option value="inactive" disabled={wabaDetail.waba_status === 'inactive'}>Set Inactive</Select.Option>
                      </Select>
                    </Space>
                  </Space>
                ) : (
                  !loadingWaba && (
                    <Alert
                      type="info"
                      showIcon
                      message="No WABA configuration found"
                      description={
                        <Space direction="vertical" size={8} style={{ width: '100%' }}>
                          <span>This company has not completed WhatsApp Business Account setup yet.</span>
                          <Button
                            type="primary"
                            size="small"
                            icon={<WhatsAppOutlined />}
                            loading={subscribingWebhooks}
                            style={{ backgroundColor: '#25D366', borderColor: '#25D366' }}
                            onClick={() => confirmSubscribeWabaWebhooks(companyDetailDrawer.company.companyid)}
                          >
                            Subscribe WABA Webhooks
                          </Button>
                        </Space>
                      }
                    />
                  )
                )}
              </Spin>
            </Card>

          </Space>
        )}
      </Drawer>

    </div>
  );
};

export default SuperAdminDashboard;
